import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { mapWithConcurrency } from "../../../utilis/mapWithConcurrency";
import { dailyActivityApi } from "./dailyActivityApi";
import { mapApiTaskToFrontend, mapModalFormToCreatePayload, mapModalFormToUpdatePayload } from "./dailyActivityMappers";
import {
  CMS_PRIMARY,
  DAILY_LOG_SWEETALERT,
} from "./DailyActivityLoggerConstants";
import {
  orderDailyTasks,
  reorderPendingTasks,
  validateAllDailyTasks,
  computeDailyLogSummary,
  todayIsoDate,
  countReportBuckets,
  filterTodayReportTasks,
} from "./DailyActivityLoggerUtils";

const swalConfirm = ({ title, text, html, confirmButtonText, isDanger = false }) =>
  Swal.fire({
    title,
    text,
    html,
    icon: isDanger ? "warning" : "question",
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Cancel",
    confirmButtonColor: isDanger ? "#dc2626" : CMS_PRIMARY,
    reverseButtons: true,
    focusCancel: true,
  });

export function useDailyActivityLogger() {
  const today = todayIsoDate();
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [isFinalized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const loadTasks = useCallback(async (date) => {
    setLoading(true);
    try {
      // Prefer the dedicated today-tasks endpoint when loading today's log
      const res =
        date === today
          ? await dailyActivityApi.getTodayTasks()
          : await dailyActivityApi.getTasks(date);

      const payload = res?.data?.data;
      setTasks((payload?.tasks || []).map(mapApiTaskToFrontend));
    } catch (err) {
      console.error(err);
      setTasks([]);
      Swal.fire({
        title: "Error",
        text: "Could not load tasks.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  }, [today]);

  // Load Today once on mount / when selectedDate changes only
  useEffect(() => {
    loadTasks(selectedDate);
  }, [selectedDate, loadTasks]);

  useEffect(() => setBannerDismissed(false), [selectedDate]);

  const orderedTasks = useMemo(() => orderDailyTasks(tasks), [tasks]);
  const summary = useMemo(
    () => computeDailyLogSummary(orderedTasks),
    [orderedTasks]
  );
  const validationErrors = useMemo(
    () => validateAllDailyTasks(orderedTasks),
    [orderedTasks]
  );

  const todayReportTasks = useMemo(
    () => filterTodayReportTasks(orderedTasks, selectedDate),
    [orderedTasks, selectedDate]
  );

  const overdueTasks = useMemo(
    () =>
      orderedTasks.filter(
        (t) =>
          !t.completed &&
          !t.is_finalized &&
          t.scheduled_date &&
          t.scheduled_date < selectedDate
      ),
    [orderedTasks, selectedDate]
  );

  const isAllSelected = useCallback(
    (selectableIds) =>
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedIds.has(id)),
    [selectedIds]
  );

  const toggleSelectAll = useCallback((selectableIds) => {
    setSelectedIds((prev) => {
      const allSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(selectableIds);
    });
  }, []);

  const createTaskFromModal = useCallback(
    async (formData, context = "today") => {
      try {
        await dailyActivityApi.createTask(
          mapModalFormToCreatePayload(formData, context, today)
        );

        return true;
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: err?.response?.data?.message || "Could not create task.",
          icon: "error",
        });
        return false;
      }
    },
    [today]
  );

  const updateTaskFromModal = useCallback(
    async (taskId, formData, context = "today") => {
      try {
        const payload = mapModalFormToUpdatePayload(formData, context, today);
        const { status, is_completed, progress_percent } = payload;

        await dailyActivityApi.updateTask(taskId, payload);

        await dailyActivityApi.updateStatus(taskId, {
          status,
          is_completed,
          progress_percent,
        });

        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  description: payload.description,
                  priority: payload.priority,
                  outcome: payload.note || "",
                  status,
                  progress_percent,
                  completed: is_completed,
                  completed_at: is_completed
                    ? t.completed_at || new Date().toISOString()
                    : null,
                  scheduled_date:
                    context === "future"
                      ? formData.scheduled_date || t.scheduled_date
                      : t.scheduled_date,
                }
              : t
          )
        );

        return true;
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: err?.response?.data?.message || "Could not update task.",
          icon: "error",
        });
        return false;
      }
    },
    [today]
  );

  const rescheduleAllOverdue = useCallback(async () => {
    const ids = overdueTasks.map((t) => t.id);
    setTasks((prev) =>
      prev.map((t) =>
        ids.includes(t.id) ? { ...t, scheduled_date: selectedDate } : t
      )
    );
    await mapWithConcurrency(ids, (id) =>
      dailyActivityApi
        .updateTask(id, { due_date: selectedDate })
        .catch(console.error)
    );
  }, [overdueTasks, selectedDate]);

  const dismissOverdueBanner = useCallback(() => setBannerDismissed(true), []);

  const requestDeleteTask = useCallback((id) => {
    swalConfirm(DAILY_LOG_SWEETALERT.delete).then(async (r) => {
      if (!r.isConfirmed) return;
      try {
        await dailyActivityApi.deleteTask(id);
        setTasks((prev) => prev.filter((t) => t.id !== id));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: err?.response?.data?.message || "Could not delete.",
          icon: "error",
        });
      }
    });
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkDelete = useCallback(
    (onSuccess) => {
      swalConfirm({
        ...DAILY_LOG_SWEETALERT.bulkDelete,
        text: `${selectedIds.size} task(s) will be permanently deleted.`,
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        const ids = Array.from(selectedIds);
        await mapWithConcurrency(ids, (id) =>
          dailyActivityApi.deleteTask(id).catch(console.error)
        );
        setTasks((prev) => prev.filter((t) => !selectedIds.has(t.id)));
        clearSelection();
        if (typeof onSuccess === "function") onSuccess();
      });
    },
    [selectedIds, clearSelection]
  );

  const bulkComplete = useCallback(
    (onSuccess) => {
      swalConfirm(DAILY_LOG_SWEETALERT.bulkComplete).then(async (r) => {
        if (!r.isConfirmed) return;
        const now = new Date().toISOString();

        await mapWithConcurrency(Array.from(selectedIds), (id) =>
          dailyActivityApi
            .updateStatus(id, {
              is_completed: true,
              status: "Completed",
              progress_percent: 100,
            })
            .catch(console.error)
        );

        setTasks((prev) =>
          prev.map((t) =>
            selectedIds.has(t.id)
              ? {
                  ...t,
                  completed: true,
                  completed_at: now,
                  status: "Completed",
                  progress_percent: 100,
                }
              : t
          )
        );
        clearSelection();
        if (typeof onSuccess === "function") onSuccess();
      });
    },
    [selectedIds, clearSelection]
  );

  const reorderTasks = useCallback((activeId, overId) => {
    setTasks((prev) => {
      const next = reorderPendingTasks(prev, activeId, overId);
      const orderedIds = next.filter((t) => !t.completed).map((t) => t.id);
      dailyActivityApi.reorder(orderedIds).catch(console.error);
      return next;
    });
  }, []);

  const sendTodayReport = useCallback(
    (onSuccess) => {
      if (todayReportTasks.length === 0) {
        Swal.fire({
          title: "Nothing to report",
          text: "There are no tasks for today.",
          icon: "info",
        });
        return;
      }

      const { completed, inProgress, notStarted } =
        countReportBuckets(todayReportTasks);

      swalConfirm({
        title: "Send Today Report?",
        html: `
          <div style="text-align:left; font-size:14px; line-height:1.5; color:#374151;">
            <p style="margin-bottom:12px;">You're about to send today's work summary:</p>
            <div style="background:#f8fafc; border-radius:8px; padding:12px 14px; margin-bottom:12px;">
              <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                <span>Completed</span>
                <strong style="color:#16a34a;">${completed}</strong>
              </div>
              <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                <span>In Progress</span>
                <strong style="color:#2563eb;">${inProgress}</strong>
              </div>
              <div style="display:flex; justify-content:space-between;">
                <span>Not Started</span>
                <strong style="color:#d97706;">${notStarted}</strong>
              </div>
            </div>
            <p style="font-size:13px; color:#6b7280; margin:0;">
              Only today's tasks are included in this report.
            </p>
          </div>
        `,
        confirmButtonText: "Send Today Report",
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        setSendingReport(true);
        try {
          const res = await dailyActivityApi.sendTodayReport();
          const data = res?.data?.data;
          clearSelection();

          const finalCompleted = data?.report?.completed ?? data?.summary?.completed ?? completed;
          const finalInProgress = data?.report?.in_progress ?? data?.summary?.in_progress ?? inProgress;
          const finalNotStarted = data?.report?.not_started ?? data?.summary?.not_started ?? notStarted;
          const finalTotal = data?.report?.total_tasks ?? data?.summary?.total_today ?? todayReportTasks.length;

          Swal.fire({
            title: "Report Sent!",
            html: `
              <div style="text-align:left; font-size:14px; line-height:1.5; color:#374151;">
                <div style="background:#f0fdf4; border-radius:8px; padding:12px 14px; margin-bottom:14px;">
                  <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                    <span>Total tasks</span>
                    <strong style="color:#0f172a;">${finalTotal}</strong>
                  </div>
                  <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                    <span>Completed</span>
                    <strong style="color:#16a34a;">${finalCompleted}</strong>
                  </div>
                  <div style="display:flex; justify-content:space-between; margin-bottom:4px;">
                    <span>In Progress</span>
                    <strong style="color:#2563eb;">${finalInProgress}</strong>
                  </div>
                  <div style="display:flex; justify-content:space-between;">
                    <span>Not Started</span>
                    <strong style="color:#d97706;">${finalNotStarted}</strong>
                  </div>
                </div>
                <p style="font-size:13px; color:#6b7280; margin:0; text-align:center;">
                  💡 Ready for tomorrow?<br/>
                  Add tasks in <strong>Plan for Tomorrow</strong>
                </p>
              </div>
            `,
            icon: "success",
            confirmButtonText: "Got it",
            confirmButtonColor: CMS_PRIMARY,
          });

          if (typeof onSuccess === "function") onSuccess();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.message || "Could not send today report.",
            icon: "error",
          });
        } finally {
          setSendingReport(false);
        }
      });
    },
    [todayReportTasks, clearSelection]
  );

  const sendTomorrowPlan = useCallback(
    (tomorrowCount, onSuccess) => {
      if (!tomorrowCount) {
        Swal.fire({
          title: "Nothing to submit",
          text: "There are no tasks planned for tomorrow yet.",
          icon: "info",
        });
        return;
      }

      swalConfirm({
        title: "Send Tomorrow Plan?",
        text: `You’re about to submit your plan for tomorrow (${tomorrowCount} task${tomorrowCount === 1 ? "" : "s"}).`,
        confirmButtonText: "Send Tomorrow Plan",
      }).then(async (r) => {
        if (!r.isConfirmed) return;
        setSendingReport(true);

        try {
          await dailyActivityApi.sendTomorrowPlan();
          clearSelection();

          Swal.fire({
            title: "Plan submitted!",
            text: "Your tomorrow plan has been updated for the team report.",
            icon: "success",
            confirmButtonText: "Got it",
            confirmButtonColor: CMS_PRIMARY,
          });

          if (typeof onSuccess === "function") onSuccess();
        } catch (err) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.message || "Could not submit tomorrow plan.",
            icon: "error",
          });
        } finally {
          setSendingReport(false);
        }
      });
    },
    [clearSelection]
  );

  return {
    tasks: orderedTasks,
    summary,
    selectedDate,
    setSelectedDate,
    isFinalized,
    loading,
    validationErrors,
    createTaskFromModal,
    updateTaskFromModal,
    requestDeleteTask,
    sendTodayReport,
    sendTomorrowPlan,
    selectedIds,
    isAllSelected,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    bulkComplete,
    bulkDelete,
    reorderTasks,
    rescheduleAllOverdue,
    overdueCount: overdueTasks.length,
    showOverdueBanner: overdueTasks.length > 0 && !bannerDismissed,
    dismissOverdueBanner,
    loadTasks,
    sendingReport,
  };
}